/**
 * Staging must use an explicit API origin. Production is not a fallback.
 */

const PRODUCTION_API_ORIGIN = 'https://api.xpherium.com';

function normalizeBase(value = '') {
  return String(value || '').replace(/\/+$/, '');
}

function isLocalApiBase(apiBase = '') {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(String(apiBase || ''));
}

function isProductionApiBase(apiBase = '') {
  const normalized = normalizeBase(apiBase).toLowerCase();
  return normalized === `${PRODUCTION_API_ORIGIN}/api` || normalized === PRODUCTION_API_ORIGIN;
}

function stagingApiConfigured(apiBase) {
  const normalized = normalizeBase(apiBase);
  return Boolean(normalized) && !isProductionApiBase(normalized) && !isLocalApiBase(normalized);
}

/**
 * Point a hardcoded production API URL at the origin this build was given.
 * Production stays on the production host. An empty configured origin is
 * returned unchanged so the caller can fail closed instead of sending it.
 */
function rewriteConfiguredApiUrl(rawUrl, configuredBase) {
  const url = String(rawUrl || '');
  const base = normalizeBase(configuredBase);
  if (!url || !base || isProductionApiBase(base)) return url;
  const match = url.match(/^(https?:\/\/api\.xpherium\.com)(\/api)?/i);
  if (!match) return url;
  return `${base}${url.slice(match[0].length)}`;
}

function resolveApiRequestUrl({ url, configuredBase, channel = '' } = {}) {
  const raw = String(url || '');
  if (!/api\.xpherium\.com/i.test(raw)) return raw;
  if (channel === 'staging' && !stagingApiConfigured(configuredBase)) {
    throw new Error('Staging API origin is not configured.');
  }
  return rewriteConfiguredApiUrl(raw, configuredBase);
}

function apiBaseCandidates({
  channel = '',
  preferred = '',
  current = '',
  localCandidates = [],
  production = `${PRODUCTION_API_ORIGIN}/api`,
} = {}) {
  const staging = channel === 'staging';
  const chosen = normalizeBase(current || preferred);
  if (staging && !stagingApiConfigured(chosen)) {
    return [];
  }
  const candidates = [chosen, normalizeBase(preferred), ...localCandidates.map(normalizeBase)];
  const preferredBase = normalizeBase(preferred);
  const productionBase = normalizeBase(production);
  if (!staging && (preferredBase === productionBase || isLocalApiBase(preferredBase))) {
    candidates.push(productionBase);
  }
  return [...new Set(candidates.filter(Boolean))];
}

export {
  PRODUCTION_API_ORIGIN,
  normalizeBase,
  isLocalApiBase,
  isProductionApiBase,
  stagingApiConfigured,
  apiBaseCandidates,
  rewriteConfiguredApiUrl,
  resolveApiRequestUrl,
};
